<template>
  <ComponentCard :title="spec.title" :icon="spec.icon">
    <BlockField v-for="field in spec.fields ?? []" :key="field.key" :model-value="attrs[field.key]" :label="field.label ?? ''" :placeholder="field.placeholder" :read-only="readOnly" :input-class="field.big ? 'font-display text-2xl font-bold text-[#1c4848]' : 'text-sm text-[#1c4848]'" @update:model-value="set({ [field.key]: $event })" />
    <div v-if="spec.image" class="flex items-center gap-3">
      <img v-if="attrs.image" :src="attrs.image" alt="" class="h-20 w-20 shrink-0 rounded-xl object-cover" draggable="false">
      <ImagePicker v-if="!readOnly" :context="context" :has-image="Boolean(attrs.image)" compact @picked="set({ image: $event })" />
    </div>
    <OptionGroup v-for="option in readOnly ? [] : spec.options ?? []" :key="option.key" :label="option.label" :value="attrs[option.key]" :options="option.values" @pick="set({ [option.key]: $event })" />
    <template v-if="spec.items">
      <div v-for="(item, i) in attrs.items" :key="i" class="flex items-start gap-3 rounded-xl bg-[#1c4848]/[0.03] p-2">
        <div class="min-w-0 flex-1">
          <BlockField v-for="field in spec.items.fields" :key="field.key" :model-value="item[field.key]" :placeholder="field.placeholder" :read-only="readOnly" :input-class="field.bold ? 'text-sm font-bold text-[#1c4848]' : 'text-sm text-[#1c4848]/80'" @update:model-value="list.patch(i, { [field.key]: $event })" />
          <div v-if="!readOnly" class="flex flex-wrap items-center gap-2 px-2">
            <ImagePicker v-if="spec.items.image" :context="context" :has-image="Boolean(item.image)" compact @picked="list.patch(i, { image: $event })" />
            <RowActions :first="i === 0" :last="i === attrs.items.length - 1" @up="list.move(i, -1)" @down="list.move(i, 1)" @remove="list.remove(i)" />
          </div>
        </div>
        <img v-if="item.image" :src="item.image" alt="" class="h-14 w-14 shrink-0 rounded-full object-cover" draggable="false">
      </div>
      <button v-if="!readOnly" type="button" class="min-h-9 rounded-lg px-2 text-xs font-bold text-[#1c4848]/60 hover:bg-[#1c4848]/5 hover:text-[#1c4848]" @click="list.add(blankRow)">+ {{ spec.items.add }}</button>
    </template>
    <template v-if="spec.stops">
      <div v-for="(stop, i) in attrs.stops" :key="i" class="flex items-center gap-1">
        <div class="min-w-0 flex-1"><BlockField :model-value="stop" placeholder="A stop on the way" :read-only="readOnly" @update:model-value="stops.change(i, $event)" /></div>
        <RowActions v-if="!readOnly" :first="i === 0" :last="i === attrs.stops.length - 1" @up="stops.move(i, -1)" @down="stops.move(i, 1)" @remove="stops.remove(i)" />
      </div>
      <button v-if="!readOnly && attrs.stops.length < 6" type="button" class="min-h-9 rounded-lg px-2 text-xs font-bold text-[#1c4848]/60 hover:bg-[#1c4848]/5 hover:text-[#1c4848]" @click="stops.add('')">+ Add a stop</button>
    </template>
  </ComponentCard>
</template>

<script setup>
import { computed } from 'vue';
import BlockField from '@/components/writer/views/BlockField.vue';
import ComponentCard from '@/components/writer/views/ComponentCard.vue';
import ImagePicker from '@/components/writer/views/ImagePicker.vue';
import OptionGroup from '@/components/writer/views/OptionGroup.vue';
import RowActions from '@/components/writer/views/RowActions.vue';
import { useItems } from '@/components/writer/views/useItems.js';

const TONES = (...values) => values.map((value) => ({ value, label: value[0].toUpperCase() + value.slice(1) }));
const SPECS = {
  heroNumber: {
    title: 'Number on a picture', icon: 'solar:hashtag-square-linear', image: true,
    fields: [{ key: 'kicker', placeholder: 'Small line above' }, { key: 'value', placeholder: '17', big: true }, { key: 'unit', placeholder: 'What it counts' }, { key: 'text', placeholder: 'A sentence (optional)' }],
  },
  roundList: {
    title: 'List with round pictures', icon: 'solar:users-group-rounded-linear',
    fields: [{ key: 'title', placeholder: 'Title (optional)' }],
    items: { add: 'Add a row', image: true, blank: { title: '', text: '', value: '', image: '' }, fields: [{ key: 'title', placeholder: 'Name', bold: true }, { key: 'text', placeholder: 'Line under it (optional)' }, { key: 'value', placeholder: 'Number on the right (optional)' }] },
  },
  checklist: {
    title: 'Checklist', icon: 'solar:checklist-minimalistic-linear',
    fields: [{ key: 'title', placeholder: 'Title' }],
    items: { add: 'Add a step', blank: { title: '', text: '' }, fields: [{ key: 'title', placeholder: 'What to do', bold: true }, { key: 'text', placeholder: 'More about it (optional)' }] },
  },
  curve: {
    title: 'Path with stops', icon: 'solar:graph-up-linear', stops: true,
    fields: [{ key: 'kicker', placeholder: 'Small line above' }, { key: 'value', placeholder: '10', big: true }, { key: 'unit', placeholder: 'What it counts' }, { key: 'text', placeholder: 'A sentence (optional)' }],
    options: [{ key: 'tone', label: 'Colour', values: TONES('peach', 'sky', 'teal') }],
  },
  action: {
    title: 'Card with a button', icon: 'solar:link-round-linear', image: true,
    fields: [{ key: 'kicker', placeholder: 'Small line above' }, { key: 'title', placeholder: 'Title' }, { key: 'text', placeholder: 'A few lines' }, { key: 'label', placeholder: 'Button text' }, { key: 'url', placeholder: 'https://…' }],
    options: [{ key: 'tone', label: 'Colour', values: TONES('dark', 'teal', 'sky', 'peach') }],
  },
  spotlight: {
    title: 'Circle spotlight', icon: 'solar:target-linear', image: true,
    fields: [{ key: 'title', placeholder: 'Title' }, { key: 'meta', placeholder: 'Line under it' }, { key: 'text', placeholder: 'A few lines' }],
    items: { add: 'Add a detail', blank: { value: '', label: '' }, fields: [{ key: 'value', placeholder: 'Value', bold: true }, { key: 'label', placeholder: 'What it is' }] },
  },
  statement: {
    title: 'Dark statement', icon: 'solar:moon-stars-linear', image: true,
    fields: [{ key: 'title', placeholder: 'A big sentence', big: true }, { key: 'accent', label: 'Words printed in gold', placeholder: 'Copy them from the sentence' }, { key: 'text', placeholder: 'A few lines' }],
  },
  deck: {
    title: 'Stacked cards', icon: 'solar:layers-linear',
    items: { add: 'Add a card', image: true, blank: { kicker: '', title: '', text: '', image: '' }, fields: [{ key: 'kicker', placeholder: 'Small line' }, { key: 'title', placeholder: 'Title', bold: true }, { key: 'text', placeholder: 'A few lines' }] },
  },
};

const props = defineProps({ attrs: Object, readOnly: Boolean, context: Object, set: Function, kind: String });
const spec = computed(() => SPECS[props.kind]);
const blankRow = computed(() => spec.value.items.blank);
const list = useItems(props);
const stops = useItems(props, 'stops');
</script>
